/**
 * Phase 27.53.S — Minimal rich text editor for the Marketing Promo body.
 *
 * Supports Bold, Italic, Underline, bullet list and numbered list. Uses a
 * contentEditable surface + `document.execCommand`, which is deprecated in
 * spec but still universally supported and perfect for an admin-only KR
 * composer. The parent controls the HTML value; sanitisation happens on
 * the backend (bleach) before rendering into the email template.
 */
import React, { useEffect, useRef } from 'react';
import { Bold, Italic, Underline, List, ListOrdered } from 'lucide-react';

const BUTTONS = [
  { cmd: 'bold', icon: Bold, label: 'Bold', shortcut: '⌘B' },
  { cmd: 'italic', icon: Italic, label: 'Italic', shortcut: '⌘I' },
  { cmd: 'underline', icon: Underline, label: 'Underline', shortcut: '⌘U' },
  { cmd: 'insertUnorderedList', icon: List, label: 'Bullet list' },
  { cmd: 'insertOrderedList', icon: ListOrdered, label: 'Numbered list' },
];

export default function RichTextEditor({ value, onChange, placeholder, minHeight = 160, testId }) {
  const ref = useRef(null);

  // Sync external value → editor only when it changes from the outside
  // (avoid caret jumps on every keystroke).
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (cmd) => {
    ref.current?.focus();
    document.execCommand(cmd, false, null);
    onChange?.(ref.current?.innerHTML || '');
  };

  const handleInput = () => onChange?.(ref.current?.innerHTML || '');

  const handlePaste = (e) => {
    // Strip formatting on paste so admins don't inherit weird styling from
    // Word / Google Docs. They can re-apply B/I/U/lists from the toolbar.
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <div
      className="rounded-md border border-slate-200 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-purple-200 focus-within:border-purple-400"
      data-testid={testId}
    >
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-slate-100 bg-slate-50">
        {BUTTONS.map(({ cmd, icon: Icon, label, shortcut }) => (
          <button
            key={cmd}
            type="button"
            title={shortcut ? `${label} (${shortcut})` : label}
            onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
            className="p-1.5 rounded hover:bg-slate-200 text-slate-600 hover:text-slate-900 transition"
            data-testid={`rte-${cmd}`}
          >
            <Icon size={14} />
          </button>
        ))}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        role="textbox"
        aria-multiline="true"
        aria-placeholder={placeholder}
        data-placeholder={placeholder}
        className="rte-surface px-3 py-2 text-sm text-slate-800 focus:outline-none"
        style={{ minHeight }}
      />
      <style>{`
        .rte-surface:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
        }
        .rte-surface ul { list-style: disc; padding-left: 22px; margin: 0 0 8px; }
        .rte-surface ol { list-style: decimal; padding-left: 22px; margin: 0 0 8px; }
        .rte-surface li { margin-bottom: 2px; }
        .rte-surface p { margin: 0 0 8px; }
      `}</style>
    </div>
  );
}
